/**
 * Utility functions for data validation and filtering
 */

/**
 * Checks if a value is empty (null, undefined, empty string, empty array, empty object)
 * @param {any} value - The value to check
 * @returns {boolean} - True if the value is empty
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * Checks if an object has any non-empty values
 * @param {Object} obj - The object to check
 * @returns {boolean} - True if object has at least one non-empty value
 */
const hasNonEmptyValue = (obj) => {
  if (!obj || typeof obj !== 'object') return false;
  for (const value of Object.values(obj)) {
    if (value !== null && value !== undefined && value !== '') {
      if (typeof value === 'string' && value.trim() !== '') return true;
      if (typeof value !== 'string') return true;
    }
  }
  return false;
};

/**
 * Filters empty items from an array
 * Works with arrays of any type (primitives, objects, nested arrays)
 * For objects: removes items where all values are empty (null, undefined, empty string)
 * For primitives: removes null, undefined, and empty strings
 * For arrays: removes empty arrays
 * 
 * @param {Array} arr - The array to filter
 * @param {Object} options - Filtering options
 * @param {boolean} options.removeEmptyObjects - Remove objects with all empty values (default: true)
 * @param {boolean} options.removeEmptyStrings - Remove empty strings (default: true)
 * @param {boolean} options.removeNull - Remove null values (default: true)
 * @param {boolean} options.removeUndefined - Remove undefined values (default: true)
 * @param {Function} options.customFilter - Custom filter function (item) => boolean
 * @returns {Array} - Filtered array (returns undefined if array becomes empty)
 */
export const filterEmptyArrayItems = (arr, options = {}) => {
  if (!Array.isArray(arr)) return arr;
  if (arr.length === 0) return undefined;
  
  const {
    removeEmptyObjects = true,
    removeEmptyStrings = true,
    removeNull = true,
    removeUndefined = true,
    customFilter = null,
  } = options;
  
  const filtered = arr.filter(item => {
    // Apply custom filter if provided
    if (customFilter && typeof customFilter === 'function') {
      if (!customFilter(item)) return false;
    }
    
    // Handle null
    if (item === null && removeNull) return false;
    
    // Handle undefined
    if (item === undefined && removeUndefined) return false;
    
    // Handle empty strings
    if (typeof item === 'string' && item.trim() === '' && removeEmptyStrings) return false;
    
    // Handle empty arrays
    if (Array.isArray(item) && item.length === 0) return false;
    
    // Handle objects
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      if (removeEmptyObjects && !hasNonEmptyValue(item)) return false;
    }
    
    return true;
  });
  
  return filtered.length > 0 ? filtered : undefined;
};

/**
 * Removes empty values from an object
 * @param {Object} obj - The object to clean
 * @param {Object} options - Options for cleaning
 * @param {boolean} options.removeEmptyStrings - Whether to remove empty strings (default: true)
 * @param {boolean} options.removeEmptyArrays - Whether to remove empty arrays (default: true)
 * @param {boolean} options.removeEmptyObjects - Whether to remove empty objects (default: true)
 * @param {boolean} options.removeNull - Whether to remove null values (default: true)
 * @param {boolean} options.removeUndefined - Whether to remove undefined values (default: true)
 * @returns {Object} - The cleaned object
 */
export const removeEmptyValues = (obj, options = {}) => {
  const {
    removeEmptyStrings = true,
    removeEmptyArrays = true,
    removeEmptyObjects = true,
    removeNull = true,
    removeUndefined = true,
  } = options;

  if (!obj || typeof obj !== 'object') return obj;

  const cleaned = {};

  for (const [key, value] of Object.entries(obj)) {
    // Skip if value is null and we're removing nulls
    if (value === null && removeNull) continue;
    
    // Skip if value is undefined and we're removing undefined
    if (value === undefined && removeUndefined) continue;
    
    // Skip if value is empty string and we're removing empty strings
    if (typeof value === 'string' && value.trim() === '' && removeEmptyStrings) continue;
    
    // Skip if value is empty array and we're removing empty arrays
    if (Array.isArray(value) && value.length === 0 && removeEmptyArrays) continue;
    
    // Skip if value is empty object and we're removing empty objects
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0 && removeEmptyObjects) continue;

    // Recursively clean nested objects
    if (typeof value === 'object' && !Array.isArray(value)) {
      const cleanedValue = removeEmptyValues(value, options);
      if (Object.keys(cleanedValue).length > 0) {
        cleaned[key] = cleanedValue;
      }
    } else {
      cleaned[key] = value;
    }
  }

  return cleaned;
};

/**
 * Compares two objects and returns only the changed fields
 * @param {Object} original - The original object
 * @param {Object} updated - The updated object
 * @param {Object} options - Options for comparison
 * @param {boolean} options.includeNewFields - Whether to include new fields (default: true)
 * @param {boolean} options.removeEmptyValues - Whether to remove empty values from result (default: true)
 * @returns {Object} - Object containing only the changed fields
 */
export const getChangedFields = (original, updated, options = {}) => {
  const {
    includeNewFields = true,
    removeEmptyValues: shouldRemoveEmpty = true,
  } = options;

  if (!original || !updated) return updated || {};

  const changes = {};

  // Check for changed and new fields
  for (const [key, value] of Object.entries(updated)) {
    const originalValue = original[key];
    
    // Include if it's a new field and we want new fields
    if (includeNewFields && !(key in original)) {
      changes[key] = value;
      continue;
    }
    
    // Include if the value has changed
    if (JSON.stringify(originalValue) !== JSON.stringify(value)) {
      changes[key] = value;
    }
  }

  // Remove empty values if requested
  return shouldRemoveEmpty ? removeEmptyValues(changes) : changes;
};

/**
 * Validates and cleans form data for API submission
 * @param {Object} formData - The form data to validate
 * @param {Object} originalData - The original data (for update operations)
 * @param {Object} options - Validation options
 * @returns {Object} - Cleaned and validated data
 */
export const validateAndCleanData = (formData, originalData = null, options = {}) => {
  const {
    removeEmptyValues: shouldRemoveEmpty = true,
    onlyChangedFields = false,
    validationRules = {},
  } = options;

  let cleanedData = { ...formData };

  // Apply validation rules
  for (const [field, rules] of Object.entries(validationRules)) {
    const value = cleanedData[field];
    
    if (rules.required && isEmpty(value)) {
      throw new Error(`${field} is required`);
    }
    
    if (rules.minLength && typeof value === 'string' && value.length < rules.minLength) {
      throw new Error(`${field} must be at least ${rules.minLength} characters`);
    }
    
    if (rules.maxLength && typeof value === 'string' && value.length > rules.maxLength) {
      throw new Error(`${field} must be no more than ${rules.maxLength} characters`);
    }
    
    if (rules.pattern && typeof value === 'string' && !rules.pattern.test(value)) {
      throw new Error(`${field} format is invalid`);
    }
    
    if (rules.min && typeof value === 'number' && value < rules.min) {
      throw new Error(`${field} must be at least ${rules.min}`);
    }
    
    if (rules.max && typeof value === 'number' && value > rules.max) {
      throw new Error(`${field} must be no more than ${rules.max}`);
    }
  }

  // Get only changed fields if this is an update operation
  if (onlyChangedFields && originalData) {
    cleanedData = getChangedFields(originalData, cleanedData);
  }

  // Remove empty values if requested
  if (shouldRemoveEmpty) {
    cleanedData = removeEmptyValues(cleanedData);
  }

  return cleanedData;
};

/**
 * Common validation patterns
 */
export const validationPatterns = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^[\+]?[1-9][\d]{0,15}$/,
  url: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
  alphanumeric: /^[a-zA-Z0-9]+$/,
  numeric: /^\d+$/,
  decimal: /^\d+(\.\d+)?$/,
};

/**
 * Common validation rules
 */
export const commonValidationRules = {
  email: {
    required: true,
    pattern: validationPatterns.email,
  },
  password: {
    required: true,
    minLength: 6,
  },
  name: {
    required: true,
    minLength: 2,
    maxLength: 50,
  },
  phone: {
    pattern: validationPatterns.phone,
  },
  url: {
    pattern: validationPatterns.url,
  },
};

export default {
  isEmpty,
  removeEmptyValues,
  getChangedFields,
  validateAndCleanData,
  filterEmptyArrayItems,
  validationPatterns,
  commonValidationRules,
};

